import React, { useState, useEffect } from 'react';
import { Download, DollarSign, FileText, Users, Calendar, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PayrollReport = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.toISOString().slice(0, 7); // YYYY-MM
  
  const [filters, setFilters] = useState({ 
    month: currentMonth,
    staff_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  // Generate month options (last 12 months + next 1 month)
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    
    // Go back 12 months and forward 1 month
    for (let i = -12; i <= 1; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options.reverse(); // Most recent first
  };

  const monthOptions = getMonthOptions();

  // Fetch staff list on mount
  useEffect(() => {
    fetchStaffList();
  }, []);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStaffList(response.data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const generateReport = async () => {
    if (!filters.month) {
      setError('Please select a month');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Get attendance summary for the month
      const attendanceResponse = await axios.get(
        `${API_URL}/api/staff-attendance/monthly-summary?month=${filters.month}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const attendanceSummary = attendanceResponse.data?.staff_summary || [];
      
      // Get salary templates for all staff
      const staffWithSalary = [];
      let totalGross = 0;
      let totalDeductions = 0;
      let totalNet = 0;

      for (const staff of staffList) {
        // Filter by staff if selected
        if (filters.staff_id && staff.id !== filters.staff_id) continue;

        try {
          const salaryResponse = await axios.get(
            `${API_URL}/api/salary/templates/${staff.id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          const salaryTemplate = salaryResponse.data;
          const attendance = attendanceSummary.find(a => a.staff_id === staff.id) || {};
          
          // Calculate working days (excluding holidays)
          const workingDays = (attendance.total_marked || 0) - (attendance.holiday || 0);
          const presentDays = (attendance.present || 0) + (attendance.late || 0) + ((attendance.half_day || 0) * 0.5);
          const attendanceRatio = workingDays > 0 ? presentDays / workingDays : 0;
          
          // Calculate actual salary based on attendance
          const grossSalary = salaryTemplate.gross_salary || staff.salary || 0;
          const actualGross = Math.round(grossSalary * attendanceRatio);
          const deductions = Math.round((salaryTemplate.total_deductions || 0) * attendanceRatio);
          const netSalary = actualGross - deductions;
          
          staffWithSalary.push({
            staff_id: staff.id,
            employee_id: staff.employee_id,
            full_name: staff.full_name,
            department: staff.department,
            designation: staff.designation,
            basic_salary: salaryTemplate.basic_salary || 0,
            gross_salary: grossSalary,
            total_allowances: salaryTemplate.total_allowances || 0,
            total_deductions: salaryTemplate.total_deductions || 0,
            // Attendance
            total_days: attendance.total_marked || 0,
            present_days: attendance.present || 0,
            absent_days: attendance.absent || 0,
            half_days: attendance.half_day || 0,
            leave_days: attendance.leave || 0,
            late_days: attendance.late || 0,
            attendance_percentage: attendance.attendance_percentage || 0,
            // Actual amounts
            actual_gross: actualGross,
            actual_deductions: deductions,
            net_payable: netSalary
          });
          
          totalGross += actualGross;
          totalDeductions += deductions;
          totalNet += netSalary;
        } catch (err) {
          // Staff may not have salary template yet
          staffWithSalary.push({
            staff_id: staff.id,
            employee_id: staff.employee_id,
            full_name: staff.full_name,
            department: staff.department,
            designation: staff.designation,
            basic_salary: 0,
            gross_salary: staff.salary || 0,
            total_allowances: 0,
            total_deductions: 0,
            total_days: 0,
            present_days: 0,
            absent_days: 0,
            half_days: 0,
            leave_days: 0,
            late_days: 0,
            attendance_percentage: 0,
            actual_gross: 0,
            actual_deductions: 0,
            net_payable: 0,
            no_salary_template: true
          });
        }
      }

      setReportData({
        month: filters.month,
        month_label: formatDate(filters.month + '-01'),
        total_staff: staffWithSalary.length,
        total_gross: totalGross,
        total_deductions: totalDeductions,
        total_net: totalNet,
        staff_payroll: staffWithSalary
      });

    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!reportData || !reportData.staff_payroll.length) {
      alert('No data to export. Please generate a report first.');
      return;
    }

    // Create CSV
    const headers = [
      'Employee ID', 'Name', 'Department', 'Designation',
      'Basic Salary', 'Gross Salary', 'Allowances', 'Deductions',
      'Present Days', 'Absent Days', 'Leave Days', 'Attendance %',
      'Actual Gross', 'Actual Deductions', 'Net Payable'
    ];
    
    const rows = reportData.staff_payroll.map(s => [
      s.employee_id,
      s.full_name,
      s.department,
      s.designation,
      s.basic_salary,
      s.gross_salary,
      s.total_allowances,
      s.total_deductions,
      s.present_days,
      s.absent_days,
      s.leave_days,
      s.attendance_percentage + '%',
      s.actual_gross,
      s.actual_deductions,
      s.net_payable
    ]);

    const csvContent = [
      `Payroll Report: ${reportData.month_label}`,
      '',
      `Total Gross: ₹${reportData.total_gross.toLocaleString('en-IN')}`,
      `Total Deductions: ₹${reportData.total_deductions.toLocaleString('en-IN')}`,
      `Net Payroll: ₹${reportData.total_net.toLocaleString('en-IN')}`,
      '',
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_report_${reportData.month}.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Report</h1>
          <p className="text-sm text-gray-500 mt-1">View comprehensive payroll reports</p>
        </div>
        <button 
          onClick={exportReport}
          disabled={!reportData}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Export Report
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Gross</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {reportData ? formatCurrency(reportData.total_gross) : '₹0'}
              </p>
            </div>
            <DollarSign className="h-10 w-10 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Deductions</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {reportData ? formatCurrency(reportData.total_deductions) : '₹0'}
              </p>
            </div>
            <TrendingUp className="h-10 w-10 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Payroll</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {reportData ? formatCurrency(reportData.total_net) : '₹0'}
              </p>
            </div>
            <FileText className="h-10 w-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Staff</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {reportData ? reportData.total_staff : 0}
              </p>
            </div>
            <Users className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Select Month
            </label>
            <select 
              className="w-full px-3 py-2 border rounded-lg bg-white"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Staff Member
            </label>
            <select 
              className="w-full px-3 py-2 border rounded-lg bg-white"
              value={filters.staff_id}
              onChange={(e) => setFilters({ ...filters, staff_id: e.target.value })}
            >
              <option value="">All Staff</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.full_name} ({staff.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateReport}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Generating...
                </>
              ) : (
                'Generate Report'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Report Table */}
      {reportData && reportData.staff_payroll.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold">
              Payroll Summary - {reportData.month_label}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Salary</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual Gross</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Payable</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.staff_payroll.map((staff, index) => (
                  <tr key={staff.staff_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900">{staff.full_name}</div>
                        <div className="text-sm text-gray-500">{staff.employee_id}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div>{staff.department}</div>
                      <div className="text-xs text-gray-400">{staff.designation}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {staff.no_salary_template ? (
                        <span className="text-yellow-600 text-xs">No template</span>
                      ) : (
                        formatCurrency(staff.gross_salary)
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        {staff.present_days}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                        {staff.absent_days}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        staff.attendance_percentage >= 90 ? 'bg-green-100 text-green-800' :
                        staff.attendance_percentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {staff.attendance_percentage}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                      {formatCurrency(staff.actual_gross)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-red-600">
                      {formatCurrency(staff.actual_deductions)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(staff.net_payable)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td colSpan="6" className="px-4 py-3 text-right font-semibold">Total:</td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {formatCurrency(reportData.total_gross)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {formatCurrency(reportData.total_deductions)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-blue-600 text-lg">
                    {formatCurrency(reportData.total_net)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {loading ? 'Generating payroll report...' : 'No payroll report available. Select month and generate report.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PayrollReport;
